import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/Dialog'
import { useRouteParams } from '@/hooks/useRoutes'
import { toast } from 'sonner'

interface InviteTeamMemberDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function InviteTeamMemberDialog({
  open,
  onOpenChange,
}: InviteTeamMemberDialogProps) {
  const { withAccount } = useRouteParams()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<'admin' | 'agent'>('agent')
  const [submitting, setSubmitting] = useState(false)

  const accountParams = withAccount()

  const handleSubmit = () => {
    if (!email.trim()) {
      toast.error('Email is required')
      return
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email.trim())) {
      toast.error('Please enter a valid email address')
      return
    }

    setSubmitting(true)
    router.post(
      `/${accountParams.account_id}/invitations`,
      { invitation: { email: email.trim(), role } },
      {
        preserveScroll: true,
        onSuccess: () => {
          setEmail('')
          setRole('agent')
          onOpenChange(false)
        },
        onError: (errors) => {
          if ('authorization' in errors) return
          const errorMessages = Object.entries(errors)
            .map(([field, message]) => `${field} ${message}`)
            .join('. ')
          toast.error(errorMessages || 'Failed to send invitation')
        },
        onFinish: () => {
          setSubmitting(false)
        },
      }
    )
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setEmail('')
      setRole('agent')
    }
    onOpenChange(isOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite Team Member</DialogTitle>
          <DialogDescription>
            Send an invitation to join your team. They'll receive an email with a link to accept.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="invite-email">Email Address *</Label>
            <Input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="colleague@example.com"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleSubmit()
                }
              }}
            />
          </div>

          <div className="space-y-2">
            <Label>Role</Label>
            <Select value={role} onValueChange={(value) => setRole(value as 'admin' | 'agent')}>
              <SelectTrigger className="cursor-pointer">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="agent" className="cursor-pointer">
                  <div className="flex flex-col items-start">
                    <span>Agent</span>
                    <span className="text-xs opacity-70">Can view and respond to conversations</span>
                  </div>
                </SelectItem>
                <SelectItem value="admin" className="cursor-pointer">
                  <div className="flex flex-col items-start">
                    <span>Admin</span>
                    <span className="text-xs opacity-70">Can manage team, settings, and billing</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            className="cursor-pointer"
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            className="cursor-pointer"
            disabled={submitting}
          >
            {submitting ? 'Sending...' : 'Send Invitation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
